import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Container,
    Card,
    CardContent,
    Avatar,
    Divider,
    Alert,
    Button,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    CircularProgress
} from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import { getUsersExceptLogin, postRequest, putRequest, updatePassword } from '../../api/axios';
import { getUsername } from '../../context/AuthFunc';
import { useTranslation } from 'react-i18next';

const AdminPasswordReset = () => {
    const { t } = useTranslation('global');

    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [errMsg, setErrMsg] = useState('');
    const [succ, setSucc] = useState('');

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const username = getUsername();
            const response = await getUsersExceptLogin(username);
            setUsers(response);
        } catch (error) {
            setErrMsg('Error fetching users');
            console.error(error);
        }
    };

    const handleUserChange = (e) => {
        const selectedUserId = e.target.value;
        const user = users.find(user => user.id === selectedUserId);
        if (user) {
            setSelectedUser(user);
            setNewPassword('');
            setConfirmPassword('');
            setErrMsg('');
            setSucc('');
        }
    };

    const handleSubmit = async () => {
        if (!selectedUser) {
            setErrMsg('Please select a user');
            return;
        }

        if (!newPassword || newPassword.length < 1) {
            setErrMsg('Password is required');
            return;
        }

        if (newPassword !== confirmPassword) {
            setErrMsg('Passwords do not match');
            return;
        }

        setLoading(true);
        setErrMsg('');
        setSucc('');

        try {

            const editedUser = {
                username: selectedUser.username,
                newPassword: newPassword,
            }
            console.log(editedUser)

            const response = await updatePassword(editedUser);

            if (response.success) {
                setSucc(`Password for ${selectedUser.username} has been reset successfully`);
                setNewPassword('');
                setConfirmPassword('');
            } else {
                setErrMsg(response.message || 'Failed to reset password');
            }
        } catch (error) {
            setErrMsg('Error resetting password');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            {/* Header Section */}
            <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar
                        sx={{
                            bgcolor: 'primary.main',
                            width: 56,
                            height: 56,
                        }}
                    >
                        <LockResetIcon sx={{ fontSize: 32 }} />
                    </Avatar>
                    <Box>
                        <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                            {t('adminPasswordReset.resetPassword') || 'Reset User Password'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t('adminPasswordReset.description') || 'Change password for any user without knowing their current password'}
                        </Typography>
                    </Box>
                </Box>
                <Divider sx={{ my: 3 }} />
            </Box>

            {/* Main Card */}
            <Card elevation={2} sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: 3 }}>
                    <FormControl fullWidth sx={{ mb: 3 }}>
                        <InputLabel id="user-select-label">
                            {t('adminPasswordReset.selectUser') || 'Select User'}
                        </InputLabel>
                        <Select
                            labelId="user-select-label"
                            value={selectedUser ? selectedUser.id : ''}
                            onChange={handleUserChange}
                            label={t('adminPasswordReset.selectUser') || 'Select User'}
                        >
                            <MenuItem value="">
                                <em>{t('adminPasswordReset.selectUser') || 'Select User'}</em>
                            </MenuItem>
                            {users.map(user => (
                                <MenuItem key={user.id} value={user.id}>
                                    {user.username}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {selectedUser && (
                        <>
                            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    {t('adminPasswordReset.resettingPasswordFor') || 'Resetting password for'}: <strong>{selectedUser.username}</strong>
                                </Typography>
                            </Box>

                            <TextField
                                fullWidth
                                label={t('adminPasswordReset.newPassword') || 'New Password'}
                                type="password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                sx={{ mb: 2 }}
                            />

                            <TextField
                                fullWidth
                                label={t('adminPasswordReset.confirmPassword') || 'Confirm New Password'}
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                sx={{ mb: 3 }}
                            />

                            <Button
                                fullWidth
                                variant="contained"
                                color="primary"
                                onClick={handleSubmit}
                                disabled={loading || !newPassword || !confirmPassword}
                                sx={{ py: 1.5 }}
                            >
                                {loading ? (
                                    <CircularProgress size={24} color="inherit" />
                                ) : (
                                    t('adminPasswordReset.resetPassword') || 'Reset Password'
                                )}
                            </Button>
                        </>
                    )}
                </CardContent>
            </Card>

            {/* Error and Success Messages */}
            <Box sx={{ mt: 3 }}>
                {errMsg && (
                    <Alert
                        severity="error"
                        onClose={() => setErrMsg('')}
                        sx={{ mb: 2, borderRadius: 2 }}
                    >
                        {errMsg}
                    </Alert>
                )}
                {succ && (
                    <Alert
                        severity="success"
                        onClose={() => setSucc('')}
                        sx={{ mb: 2, borderRadius: 2 }}
                    >
                        {succ}
                    </Alert>
                )}
            </Box>
        </Container>
    );
};

export default AdminPasswordReset;

